/**
 * Runtime environment detection for Buzztime Bingo.
 *
 * Every app URL is derived from `window.location` at runtime instead of being
 * hardcoded and rewritten at deploy time. One build therefore works on
 * localhost, dev and production:
 *
 *   localhost              -> each app on its own port, gameroom dev API
 *   bingo.dev.buzztime.com -> path-prefixed apps, gameroom.dev.buzztime.com
 *   bingo.buzztime.com     -> path-prefixed apps, gameroom.buzztime.com
 */

const LOCAL_HOSTNAMES = ['localhost', '127.0.0.1', '0.0.0.0'];

const hostname = typeof window !== 'undefined' ? window.location.hostname : '';
const origin = typeof window !== 'undefined' ? window.location.origin : '';

/** True when served by `react-scripts start` / `serve` on a local port. */
export const isLocal = LOCAL_HOSTNAMES.includes(hostname);

/** True on the dev tier (`*.dev.buzztime.com`). */
export const isDevEnvironment = hostname.includes('.dev.');

// Deployed apps share a single origin behind nginx path prefixes; locally each
// app has its own port. Port numbers match the `dev` scripts in apps/*/package.json.
const localUrl = (port) => `http://localhost:${port}`;

export const BINGO_URL_HOST = isLocal ? localUrl(3000) : `${origin}/host`;
export const BINGO_URL_PLAYER = isLocal ? localUrl(3001) : `${origin}/player`;
export const BINGO_URL_LANDING = isLocal ? localUrl(3002) : `${origin}/landing`;
export const BINGO_URL_SPECTATOR = isLocal ? localUrl(3003) : `${origin}/spectator`;

/**
 * Gameroom (game hub) origin. Derived by swapping the `bingo.` subdomain for
 * `gameroom.`, so bingo.dev -> gameroom.dev and bingo prod -> gameroom prod.
 * Local development has no gameroom instance, so it targets the dev tier.
 */
export const GAMEROOM_URL = isLocal
  ? 'https://gameroom.dev.buzztime.com'
  : `https://${hostname.replace(/^bingo\./, 'gameroom.')}`;

/** The game hub landing page, used for post-auth redirects. */
export const GAME_HUB_URL = isLocal ? localUrl(4000) : `${GAMEROOM_URL}/`;

/** Game server websocket. Deployed builds reach it through the same origin. */
export const GAME_SERVER_URL = isLocal
  ? 'ws://localhost:4421'
  : `${origin.replace(/^http/, 'ws')}/gameserver/`;
